import { GUS_API_URL } from '@constants/externalRoutes';
import { PreferencesRecord, TenantDocument, UserDocument, UserDocumentWithAccess } from '@youscience/bpa-common';
import api from '../utils/api';

export type Subset<K> = {
  [attr in keyof K]?: K[attr] extends object
    ? Subset<K[attr]>
    : K[attr] extends object | null
    ? Subset<K[attr]> | null
    : K[attr] extends object | null | undefined
    ? Subset<K[attr]> | null | undefined
    : K[attr];
};

const getMe = async (): Promise<UserDocumentWithAccess> => {
  const response = await api(`${GUS_API_URL}/me`);

  return response?.data as UserDocumentWithAccess;
};

/**
 * Self-service profile update. Under BPA per-organization data isolation ("Data Cards"),
 * self writes need NO card reference — `PATCH /me` resolves the caller's own persona.
 *
 * Send only the fields being changed. `PATCH` is a true partial update: omitted keys are left
 * untouched and contact arrays are not cleared by omission. Do not rebuild the request body
 * from a `GET /me` response — that body is a *reconciled* view (the canonical User card merged
 * with the current org's persona card), and echoing it back writes the merged view as persona data.
 *
 * NOTE for future admin-edit flows: admin user endpoints (`PATCH /users/{userId}`,
 * `PATCH /admin/users/{userId}`) DO require an explicit card reference — `?tenantId=` or
 * `?accessId=` — and return `400 MissingCardRef` without one. This repo has no such flow today.
 *
 * @see https://youscience.atlassian.net/wiki/spaces/IG/pages/4836786177/Data+Isolation+Per-Organization+Profile+Data+Cards+API+Integration+Guide
 */
const updateMe = async (data: Subset<UserDocument>): Promise<UserDocumentWithAccess> => {
  const response = await api.patch(`${GUS_API_URL}/me`, data);

  return response?.data as UserDocumentWithAccess;
};

const getPreferences = async (): Promise<PreferencesRecord> => {
  const response = await api(`${GUS_API_URL}/me/preferences`);

  return response?.data as PreferencesRecord;
};

const setPreferences = async (id: string): Promise<PreferencesRecord> => {
  const response = await api(`${GUS_API_URL}/me/preferences`, { method: 'PATCH', data: { currentAccess: id } });

  return response?.data as PreferencesRecord;
};

const getTenant = async (tenantId: string): Promise<TenantDocument> => {
  const response = await api(`${GUS_API_URL}/tenants/${tenantId}`);

  return response?.data as TenantDocument;
};

const GusApi = {
  getMe,
  updateMe,
  getPreferences,
  setPreferences,
  getTenant,
};

export default GusApi;
